import React, { useEffect, useState } from "react";
import useTheme from "../hooks/useTheme";

const DEFAULT_SETTINGS = {
  notifications: true,
  soundAlerts: false,
  autoRefresh: true,
  refreshInterval: "30",
  dashboardDensity: "comfortable",
  defaultReportRange: "7",
  exportFormat: "csv",
};

const loadSettings = () => {
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem("app_settings") || "{}") };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 rounded-full transition ${checked ? "bg-green-600" : "bg-slate-300"}`}
  >
    <span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition ${checked ? "left-6" : "left-1"}`} />
  </button>
);

const Field = ({ label, help, children }) => (
  <div className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <p className="font-semibold text-slate-800">{label}</p>
      {help && <p className="mt-0.5 text-xs text-slate-400">{help}</p>}
    </div>
    <div className="sm:min-w-[220px]">{children}</div>
  </div>
);

const Settings = () => {
  const { dark, setDark } = useTheme();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  const setValue = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setMessage("");
  };

  const saveSettings = () => {
    localStorage.setItem("app_settings", JSON.stringify(settings));
    localStorage.setItem("notifications", JSON.stringify(settings.notifications));
    setMessage("Settings saved successfully");
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.setItem("app_settings", JSON.stringify(DEFAULT_SETTINGS));
    setMessage("Settings reset to defaults");
  };

  return (
    <div className="p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
            <p className="mt-1 text-sm text-slate-500">Control dashboard behavior, alerts, and reporting preferences.</p>
          </div>
          <button onClick={resetSettings} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Reset Defaults
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-bold text-slate-900">Experience</h2>
          <div className="mt-5 space-y-3">
            <Field label="Dark Mode" help="Toggle the app theme for this browser.">
              <div className="flex justify-end"><Toggle checked={dark} onChange={setDark} /></div>
            </Field>
            <Field label="Dashboard Density" help="Choose how compact dashboard cards and tables should feel.">
              <select value={settings.dashboardDensity} onChange={(e) => setValue("dashboardDensity", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="comfortable">Comfortable</option>
                <option value="compact">Compact</option>
                <option value="spacious">Spacious</option>
              </select>
            </Field>
            <Field label="Auto Refresh" help="Refresh dashboard data automatically while viewing live screens.">
              <div className="flex justify-end"><Toggle checked={settings.autoRefresh} onChange={(value) => setValue("autoRefresh", value)} /></div>
            </Field>
            <Field label="Refresh Interval" help="Default automatic refresh frequency.">
              <select value={settings.refreshInterval} onChange={(e) => setValue("refreshInterval", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="15">15 seconds</option>
                <option value="30">30 seconds</option>
                <option value="60">1 minute</option>
                <option value="300">5 minutes</option>
              </select>
            </Field>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-bold text-slate-900">Notifications</h2>
          <div className="mt-5 space-y-3">
            <Field label="Email Notifications" help="Receive important device and account alerts by email.">
              <div className="flex justify-end"><Toggle checked={settings.notifications} onChange={(value) => setValue("notifications", value)} /></div>
            </Field>
            <Field label="Sound Alerts" help="Play a browser sound for critical live alerts.">
              <div className="flex justify-end"><Toggle checked={settings.soundAlerts} onChange={(value) => setValue("soundAlerts", value)} /></div>
            </Field>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-bold text-slate-900">Reports</h2>
          <div className="mt-5 space-y-3">
            <Field label="Default Report Range" help="Used when opening reports and exports.">
              <select value={settings.defaultReportRange} onChange={(e) => setValue("defaultReportRange", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="1">Today</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </Field>
            <Field label="Preferred Export Format" help="Default format for generated report files.">
              <select value={settings.exportFormat} onChange={(e) => setValue("exportFormat", e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="csv">CSV</option>
                <option value="pdf">PDF</option>
              </select>
            </Field>
          </div>
        </div>

        {message && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700">{message}</div>}

        <div className="flex justify-end">
          <button onClick={saveSettings} className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white hover:bg-green-700">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
